import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';

import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';

// Previous / "12 / 140" / Next for an edit page, in the order of its list.
// Alt + left / right arrow does the same (not while typing in a box).
@Component({
    selector: 'app-record-nav',
    imports: [MatIconModule, MatTooltipModule],
    template: `
    <div class="flex items-center rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden">
      <button type="button" class="flex items-center gap-1 pl-2 pr-3 py-2.5 text-sm font-semibold text-gray-600 hover:bg-amber-50 hover:text-amber-800 disabled:opacity-35 disabled:pointer-events-none transition-colors"
        [disabled]="!prevName || disabled" (click)="prev.emit()" [matTooltip]="prevName ? 'Previous: ' + prevName + ' (Alt + ←)' : 'This is the first ' + noun" [attr.aria-label]="'Previous ' + noun">
        <mat-icon class="!text-[20px] !h-5 !w-5">chevron_left</mat-icon><span class="hidden sm:inline">Previous</span>
      </button>
      <span class="px-3 py-2.5 text-xs font-semibold text-gray-400 tabular-nums border-x border-gray-100 whitespace-nowrap" [matTooltip]="'Same order as the ' + listName + ' list'">
        @if (position >= 0) { {{ position + 1 }} <span class="text-gray-300">/</span> {{ total }} } @else { – }
      </span>
      <button type="button" class="flex items-center gap-1 pl-3 pr-2 py-2.5 text-sm font-semibold text-gray-600 hover:bg-amber-50 hover:text-amber-800 disabled:opacity-35 disabled:pointer-events-none transition-colors"
        [disabled]="!nextName || disabled" (click)="next.emit()" [matTooltip]="nextName ? 'Next: ' + nextName + ' (Alt + →)' : 'This is the last ' + noun" [attr.aria-label]="'Next ' + noun">
        <span class="hidden sm:inline">Next</span><mat-icon class="!text-[20px] !h-5 !w-5">chevron_right</mat-icon>
      </button>
    </div>
  `
})
export class RecordNavComponent {
  @Input() position = -1;
  @Input() total = 0;
  @Input() prevName: string | null = null;
  @Input() nextName: string | null = null;
  @Input() disabled = false;
  @Input() noun = 'item';
  @Input() listName = '';
  @Output() prev = new EventEmitter<void>();
  @Output() next = new EventEmitter<void>();

  @HostListener('document:keydown', ['$event'])
  onKey(event: KeyboardEvent): void {
    if (!event.altKey || this.disabled) return;
    const target = event.target as HTMLElement;
    if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
    if (event.key === 'ArrowLeft' && this.prevName) {
      event.preventDefault();
      this.prev.emit();
    } else if (event.key === 'ArrowRight' && this.nextName) {
      event.preventDefault();
      this.next.emit();
    }
  }
}

// "Leave without saving?" when there are unsaved changes; true = go ahead.
export function confirmLeave(dialog: MatDialog, dirty: boolean, currentName: string, targetName: string): Observable<boolean> {
  if (!dirty) return of(true);
  return dialog
    .open(ConfirmDialogComponent, {
      data: {
        title: 'Unsaved changes',
        message: `Leave "${currentName || 'this page'}" without saving?`,
        detail: 'Your changes here will be lost.',
        confirmText: `Go to ${targetName}`,
        tone: 'danger',
      },
    })
    .afterClosed()
    .pipe(map((result) => !!result?.confirmation));
}
